import type { ComponentType } from 'react';
import {
  AlertTriangle,
  BadgeQuestionMark,
  CircleDashed,
  Cog,
  FileCheck2,
  FileQuestion,
  FileSpreadsheet,
  FileText,
  Lock,
  Receipt,
  SquareCheckBig,
  UserCheck,
} from 'lucide-react';
import { percent, type StatusCount } from '../stats';
import Delta from './Delta';

const STATUS_ICONS: Record<string, ComponentType<{ className?: string }>> = {
  '': CircleDashed,
  Completed: SquareCheckBig,
  Working: Cog,
  'Special Handling': AlertTriangle,
  'Ask My Accountant': BadgeQuestionMark,
  'Awaiting Receipts': Receipt,
  'Awaiting Receipts & AM For Review': FileQuestion,
  'Awaiting AM For Review': UserCheck,
  '1099NEC Review': FileText,
  'Only Expenses Booked': FileCheck2,
  'Account Closed': Lock,
};

/** Shorter card titles for the longest status names */
const SHORT_LABELS: Record<string, string> = {
  'Awaiting Receipts & AM For Review': 'Awaiting Receipts & AM Review',
  'Awaiting AM For Review': 'Awaiting AM Review',
};

interface BookkeepingStatusCardsProps {
  counts: StatusCount[];
  total: number;
  loading: boolean;
  /** Last month's count per status value (null = no comparison) */
  previous?: Map<string, number> | null;
  previousTotal?: number | null;
  /** Short name of last month, e.g. "Sep" */
  previousLabel?: string;
  /** Opens Workflow V2's Bookkeeping tab filtered to the status
   *  (undefined = the whole tab) */
  onOpen: (status?: string) => void;
}

/**
 * One card per Account Status, tinted in that status's Workflow V2 row
 * colors (config/accountStatus.ts) so the card reads like the rows it
 * opens. Ends with a Total card whose bar shows the status mix.
 */
export default function BookkeepingStatusCards({
  counts,
  total,
  loading,
  previous,
  previousTotal,
  previousLabel = 'last month',
  onOpen,
}: Readonly<BookkeepingStatusCardsProps>) {
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-[repeat(auto-fill,minmax(150px,1fr))]">
      {counts.map(({ option, count }) => {
        const Icon = STATUS_ICONS[option.value] ?? CircleDashed;
        const pct = percent(count, total);
        // No Status's text color is 'inherit' — give it a real ink
        const ink =
          option.rowTextColor === 'inherit' ? '#475569' : option.rowTextColor;
        return (
          <button
            key={option.value || 'no-status'}
            type="button"
            onClick={() => onOpen(option.value)}
            disabled={loading}
            aria-label={`${option.label}: ${count} of ${total} Bookkeeping accounts. Open them in Workflow V2.`}
            title={`Open the ${count} Bookkeeping account${count === 1 ? '' : 's'} in "${option.label}"`}
            className="group relative flex min-h-[124px] flex-col overflow-hidden rounded-xl border border-slate-200 bg-white p-4 text-left shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-modern-charcoal disabled:cursor-wait"
          >
            {/* Status color accent + soft tint */}
            <span
              aria-hidden
              className="absolute inset-y-0 left-0 w-1"
              style={{ backgroundColor: option.highlightColor }}
            />
            <span
              aria-hidden
              className="absolute inset-0 opacity-60 transition-opacity group-hover:opacity-100"
              style={{
                background: `linear-gradient(135deg, ${option.rowBgColor} 0%, #ffffff 75%)`,
              }}
            />
            <span className="relative flex items-start justify-between gap-2">
              <span
                className="rounded-md p-1.5"
                style={{ backgroundColor: option.highlightColor }}
              >
                <Icon className="h-4 w-4" />
              </span>
              <span
                className={`text-2xl font-extrabold tabular-nums ${loading ? 'animate-pulse opacity-40' : ''}`}
                style={{ color: ink }}
              >
                {count}
              </span>
            </span>
            <span
              className="relative mt-2 text-sm font-semibold leading-tight"
              style={{ color: ink }}
            >
              {SHORT_LABELS[option.value] ?? option.label}
            </span>
            <span className="relative mt-auto pt-3">
              <span className="block h-1.5 w-full overflow-hidden rounded-full bg-slate-200/70">
                <span
                  className="block h-full rounded-full transition-all duration-500"
                  style={{
                    width: `${pct}%`,
                    backgroundColor: ink,
                  }}
                />
              </span>
              <span className="mt-1 flex flex-wrap items-center justify-between gap-x-2 text-xs text-slate-600">
                <span>
                  {pct}% of {total}
                </span>
                <Delta
                  current={count}
                  previous={previous ? (previous.get(option.value) ?? 0) : null}
                  label={previousLabel}
                />
              </span>
            </span>
          </button>
        );
      })}

      {/* Total */}
      <button
        type="button"
        onClick={() => onOpen()}
        disabled={loading}
        title="Open every Bookkeeping account"
        aria-label={`Total: ${total} Bookkeeping accounts. Open them all in Workflow V2.`}
        className="group relative flex min-h-[124px] flex-col rounded-xl border border-slate-300 bg-white p-4 text-left shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-modern-charcoal disabled:cursor-wait"
      >
        <span className="flex items-start justify-between gap-2">
          <span className="rounded-md bg-slate-200 p-1.5">
            <FileSpreadsheet className="h-4 w-4 text-slate-700" />
          </span>
          <span
            className={`text-2xl font-extrabold tabular-nums text-slate-900 ${loading ? 'animate-pulse opacity-40' : ''}`}
          >
            {total}
          </span>
        </span>
        <span className="mt-2 text-sm font-semibold text-slate-900">
          Total Accounts
        </span>
        <span className="mt-auto pt-3">
          <span className="flex h-1.5 w-full overflow-hidden rounded-full bg-slate-200">
            {counts
              .filter(c => c.count > 0)
              .map(({ option, count }) => (
                <span
                  key={option.value || 'no-status'}
                  className="h-full transition-all duration-500"
                  title={`${option.label}: ${count}`}
                  style={{
                    width: `${percent(count, total)}%`,
                    backgroundColor: option.highlightColor,
                  }}
                />
              ))}
          </span>
          <span className="mt-1 flex flex-wrap items-center justify-between gap-x-2 text-xs text-slate-600">
            <span>View all accounts</span>
            <Delta
              current={total}
              previous={previousTotal}
              label={previousLabel}
            />
          </span>
        </span>
      </button>
    </div>
  );
}
